import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#451a03',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#fde68a',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#d97706',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#92400e',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const ChpCommissioningServicingRecordPDF = ({ formData = {} }) => {
  const contractor = formData.contractorDetails || {};
  const site = formData.siteDetails || {};
  const chp = formData.chpSpecification || {};
  const engine = formData.engineAndLubrication || {};
  const gas = formData.gasAndCombustion || {};
  const grid = formData.electricalGridProtection || {};
  const dec = formData.declarations || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Combined Heat & Power (CHP) Service Record</Text>
            <Text style={styles.headerSubtitle}>Commercial Cogeneration Commissioning, Servicing & ENA EREC G99 Interface</Text>
          </View>
          <View style={styles.badge}>
            <Text>G99 COMPLIANT</Text>
          </View>
        </View>

        {/* 1 & 2: Contractor & Site */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Commissioning Contractor</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company:</Text>
              <Text style={styles.fieldValue}>{contractor.companyName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Lead Engineer:</Text>
              <Text style={styles.fieldValue}>{contractor.engineerName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Gas Safe Commercial:</Text>
              <Text style={styles.fieldValue}>{contractor.gasSafeLicence || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Telephone:</Text>
              <Text style={styles.fieldValue}>{contractor.telephone || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Plant Room Location</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Facility Name:</Text>
              <Text style={styles.fieldValue}>{site.siteName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Location / Level:</Text>
              <Text style={styles.fieldValue}>{site.plantRoomLocation || 'Energy Centre'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{site.siteAddress || 'N/A'}, {site.postCode || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date of Service:</Text>
              <Text style={styles.fieldValue}>{site.serviceDate || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* 3: CHP Specification */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>3. Cogeneration Unit Specification</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Make & Model:</Text>
                <Text style={styles.fieldValue}>{chp.manufacturer || 'N/A'} {chp.model || ''}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Serial Number:</Text>
                <Text style={styles.fieldValue}>{chp.serialNumber || 'N/A'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Rated Outputs:</Text>
                <Text style={styles.fieldValue}>{chp.electricalOutputKwe || 0} kWe • {chp.thermalOutputKwt || 0} kWt</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Engine Hours / Fuel:</Text>
                <Text style={styles.fieldValue}>{chp.operatingHoursTotal || 0} hrs ({chp.fuelType || 'Natural Gas'})</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 4 & 5: Engine & Gas Train */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>4. Engine & Lubrication Service</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Engine Oil Service:</Text>
              <Text style={styles.fieldValue}>{engine.oilReplaced || 'Replaced'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Oil Filter Replaced:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{engine.oilFilterReplaced || 'Yes'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Spark Plugs:</Text>
              <Text style={styles.fieldValue}>{engine.sparkPlugsGappedOrReplaced || 'Gapped'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Valve Clearances:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{engine.valveClearancesChecked || 'Within Spec'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>5. Gas Train & Flue Analysis</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Gas Pressures:</Text>
              <Text style={styles.fieldValue}>Stand: {gas.standingPressureMbar || 0} / Work: {gas.runningPressureMbar || 0} mbar</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Lambda Output / O2:</Text>
              <Text style={styles.fieldValue}>{gas.lambdaSensorVoltage || '0.85'} V</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Flue CO / NOx:</Text>
              <Text style={styles.fieldValue}>{gas.flueCoPpm || 0} ppm CO • {gas.flueNoxPpm || 0} ppm NOx</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Exhaust System Integrity:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{gas.flueExhaustSystemIntegrity || 'Pass / Gas Tight'}</Text>
            </View>
          </View>
        </View>

        {/* 6: Grid & Interlocks */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>6. ENA EREC G99 Grid Interface & Safety Interlocks</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>G99 Loss of Mains Relay:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{grid.g99ProtectionRelayTested || 'Yes - Tested'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Voltage / Frequency Trip:</Text>
                <Text style={styles.fieldValue}>{grid.underVoltageTripSec || 200}ms / {grid.underFrequencyTripSec || 250}ms</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Emergency Stop System:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{grid.emergencyStopTested || 'Pass'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Gas Solenoid Interlock:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{grid.gasSolenoidInterlockTested || 'Pass'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 7: Status & Notes */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>7. Operational Status & Next Service Schedule</Text>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Operating Status:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold', color: '#92400e' }]}>{dec.overallStatus || 'SERVICED & OPERATIONAL'}</Text>
          </View>
          <Text style={{ fontSize: 6.5, color: '#334155', marginTop: 2 }}>
            {dec.engineerComments || 'CHP cogeneration package serviced in accordance with manufacturer technical guidelines and G99 grid compliance.'}
          </Text>
          <View style={[styles.fieldRow, { marginTop: 3 }]}>
            <Text style={styles.fieldLabel}>Next Service Interval:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold' }]}>{dec.nextServiceDueHours || '8,000 Hours'}</Text>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>CHP Specialist Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{contractor.engineerName || 'Lead Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Gas Safe Commercial Cogeneration Stamp</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{site.serviceDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Plant Room Manager / Client</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{site.siteName || 'Plant Manager'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Handover received & operational log signed</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{site.serviceDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            ENA EREC G99 / IGEM UP/2 • Combined Heat and Power (CHP) Statutory Service Record
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default ChpCommissioningServicingRecordPDF;
